import React from 'react';
import { useLang } from '../context/LanguageContext';
import { PageHeader } from '../components/PageHeader';
import { ReadingMeta, SourcesBlock } from '../components/ReadingMeta';
import TableOfContents from '../components/TableOfContents';
import ContactStrip from '../components/ContactStrip';
import SEO from '../components/SEO';
import { SEO_KEYWORDS } from '../data/seo';
import { QUALITY_PARAMS, NORM_USAGE, VICAT_TEST } from '../data/science';
import { FileCheck2 } from 'lucide-react';
import { SITE_URL, CONTACT } from '../data/site';
import AnswerBlock from '../components/geo/AnswerBlock';
import Breadcrumbs from '../components/geo/Breadcrumbs';
import AuthorMeta from '../components/geo/AuthorMeta';
import RelatedTopics from '../components/geo/RelatedTopics';
import FAQ from '../components/geo/FAQ';
import ChapelleInfographic from '../components/ChapelleInfographic';
import { FAQS, RELATED, PAGE_DATES } from '../data/geo';

const QualitaetPruefung = () => {
  const { t, lang } = useLang();
  const answer = lang === 'de'
    ? 'Die Qualit\u00e4t eines Metakaolins wird durch mehrere Kennwerte gemeinsam bestimmt: Gehalt an SiO\u2082 und Al\u2082O\u2083, Reinheit (Chlorid, Sulfat, freies CaO, MgO, Gl\u00fchverlust), Reaktivit\u00e4t (modifizierter Chapelle-Wert, Aktivit\u00e4tsindex) sowie Feinheit und Wasserbedarf. Ma\u00dfgeblich ist die Gesamtbetrachtung \u2013 einzelne Werte charakterisieren die Eignung nicht abschlie\u00dfend.'
    : 'The quality of a metakaolin is determined by several parameters together: SiO\u2082 and Al\u2082O\u2083 content, purity (chloride, sulphate, free CaO, MgO, loss on ignition), reactivity (modified Chapelle value, activity index) as well as fineness and water demand. The overall picture is decisive \u2013 individual values do not conclusively characterise suitability.';
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'TechArticle',
    headline: t('qualitaet.title'), description: answer, inLanguage: lang,
    mainEntityOfPage: `${SITE_URL}/qualitaet-pruefung`,
    datePublished: PAGE_DATES.qualitaet.published, dateModified: PAGE_DATES.qualitaet.reviewed,
    author: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
    publisher: { '@type': 'Organization', name: 'NEWCHEM GmbH', url: CONTACT.website },
  };
  const toc = [
    { id: 'kennwerte', label: t('qualitaet.s1Title') },
    { id: 'norm', label: t('qualitaet.normTitle') },
    { id: 'chapelle', label: t('qualitaet.s2Title') },
    { id: 'vicat', label: t('qualitaet.vicatTitle') },
    { id: 'faq', label: 'FAQ' },
    { id: 'quellen', label: t('common.sources') },
  ];
  return (
    <div>
      <SEO title={t('qualitaet.title')} description={answer} path="/qualitaet-pruefung" lang={lang} keywords={SEO_KEYWORDS[lang]} jsonLd={jsonLd} />
      <PageHeader label={t('qualitaet.label')} title={t('qualitaet.title')} lead={t('qualitaet.lead')} imageKey="labTest1" />
      <section className="bg-white py-10 lg:py-14">
        <div className="container-wide"><Breadcrumbs items={[{ label: t('qualitaet.label'), to: '/qualitaet-pruefung' }]} /></div>
        <div className="container-wide mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4 order-2 lg:order-1">
            <div className="lg:sticky lg:top-24 space-y-5">
              <ReadingMeta minutes={t('qualitaet.readMinutes')} />
              <TableOfContents items={toc} />
              <AuthorMeta dates={PAGE_DATES.qualitaet} />
            </div>
          </aside>
          <article className="lg:col-span-8 order-1 lg:order-2">
            <AnswerBlock>{answer}</AnswerBlock>
            <div id="kennwerte" className="mt-10 scroll-mt-24 max-w-2xl">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900">{t('qualitaet.s1Title')}</h2>
              <p className="mt-4 text-[15px] text-slate-600 leading-relaxed">{t('qualitaet.s1Body')}</p>
            </div>
            <h3 className="mt-8 text-[15px] font-bold text-slate-900">{t('qualitaet.tableTitle')}</h3>
            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="w-full text-left border-collapse min-w-[460px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-[12px] font-bold uppercase tracking-wide text-slate-500">{t('common.parameter')}</th>
                    <th className="px-4 py-3 text-[12px] font-bold uppercase tracking-wide text-slate-500">{t('common.orientation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {QUALITY_PARAMS.map((p, i) => (
                    <tr key={i} className={i % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                      <td className="px-4 py-3 text-[13.5px] font-semibold text-slate-700">{p[lang]}</td>
                      <td className="px-4 py-3 text-[13.5px] text-blue-700 font-bold whitespace-nowrap">{p.orient}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div id="norm" className="mt-10 scroll-mt-24">
              <div className="flex items-start gap-4 rounded-xl border border-blue-200 bg-blue-50 px-6 py-5">
                <FileCheck2 className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('qualitaet.normTitle')}</p>
                  <p className="mt-2 text-[14.5px] text-slate-700 leading-relaxed">{t('qualitaet.normBody')}</p>
                </div>
              </div>
              <h3 className="mt-8 text-[15px] font-bold text-slate-900">{t('qualitaet.normUsageTitle')}</h3>
              <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left border-collapse min-w-[420px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-[12px] font-bold uppercase tracking-wide text-slate-500">{lang === 'de' ? 'Zementtyp' : 'Cement type'}</th>
                      <th className="px-4 py-3 text-[12px] font-bold uppercase tracking-wide text-slate-500">{lang === 'de' ? 'Zul\u00e4ssiger Ersatz' : 'Permitted replacement'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {NORM_USAGE.map((r, i) => (
                      <tr key={i} className={i % 2 ? 'bg-slate-50/50' : 'bg-white'}>
                        <td className="px-4 py-3 text-[13.5px] font-semibold text-slate-700 whitespace-nowrap">{r.cem}</td>
                        <td className="px-4 py-3 text-[13.5px] text-slate-700">{r.rate[lang]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div id="chapelle" className="mt-12 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900 max-w-2xl">{t('qualitaet.s2Title')}</h2>
              <p className="mt-4 mb-7 text-[15px] text-slate-600 leading-relaxed max-w-2xl">{t('qualitaet.s2Body')}</p>
              <h3 className="mb-4 text-[15px] font-bold text-slate-900">{t('qualitaet.chapelleTitle')}</h3>
              <ChapelleInfographic />
            </div>

            <div id="vicat" className="mt-12 scroll-mt-24">
              <h2 className="text-[24px] font-extrabold tracking-tight text-slate-900 max-w-2xl">{t('qualitaet.vicatTitle')}</h2>
              <p className="mt-4 mb-7 text-[15px] text-slate-600 leading-relaxed max-w-2xl">{t('qualitaet.vicatIntro')}</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-6">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">{t('qualitaet.vicatMixTitle')}</h3>
                  <ul className="mt-4 space-y-2 text-[13.5px] text-slate-700 list-disc pl-4">
                    {VICAT_TEST.mix[lang].map((m, i) => (<li key={i}>{m}</li>))}
                  </ul>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-6">
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">{t('qualitaet.vicatStepsTitle')}</h3>
                  <ol className="mt-4 space-y-2.5">
                    {VICAT_TEST.steps[lang].map((s, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[13.5px] text-slate-700">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-700 text-white text-[11px] font-bold shrink-0 mt-0.5">{i + 1}</span>
                        <span>{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>

            <div id="faq" className="mt-14 scroll-mt-24">
              <FAQ items={FAQS.qualitaet[lang]} />
            </div>
            <div id="quellen" className="mt-14 scroll-mt-24">
              <SourcesBlock sources={t('qualitaet.sources')} />
            </div>
            <div className="mt-12">
              <RelatedTopics items={RELATED.qualitaet} />
            </div>
          </article>
        </div>
      </section>
      <ContactStrip />
    </div>
  );
};

export default QualitaetPruefung;
